import { ArrowRight, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTopicData } from "@/hooks/useTopicData";
import type { Topic } from "@/data/topicsData";

/** 日付の無いものは時系列に置けないので末尾へ回す。あとは新しい順。 */
const byDateDesc = (a: Topic, b: Topic) => {
  if (!a.date && !b.date) return 0;
  if (!a.date) return 1;
  if (!b.date) return -1;
  return b.date.localeCompare(a.date);
};

/** 「2026/01」を <time> の datetime に使える形へ。 */
const toDateTime = (d: string) => d.replace("/", "-");

/**
 * お知らせ。ヒーローの真下に置く。
 * オーナー指示（2026-09-11）で件数は3件、本文は1〜2行に収める。
 */
export const TopicSection = () => {
  const { t, i18n } = useTranslation();
  const { topics } = useTopicData();
  const isJa = i18n.language.startsWith("ja");
  // どれを載せるかは topicsData の並び（掲載順）で決め、
  // 並べ方だけを時系列にする。順序を先に崩すと載る3件が入れ替わる。
  const shown = topics.slice(0, 3).sort(byDateDesc);

  return (
    <section className="topics" id="topics" aria-labelledby="topics-title">
      <div className="topics-inner">
        <h2 id="topics-title">
          <FileText aria-hidden="true" focusable="false" />
          {t("topics.heading")}
        </h2>
        <div className="topic-list">
          {shown.map((topic) => {
            const text = (isJa ? topic.content : topic.contentEn ?? topic.content).replace(/\n/g, " ");
            const isExternal = topic.link?.startsWith("http");
            const inner = (
              <>
                {topic.date && <time dateTime={toDateTime(topic.date)}>{topic.date}</time>}
                <span className="topic-text">{text}</span>
                <ArrowRight aria-hidden="true" focusable="false" />
              </>
            );
            if (!topic.link) {
              return (
                <span className="topic-link" key={topic.id} title={text}>
                  {inner}
                </span>
              );
            }
            return isExternal ? (
              <a
                className="topic-link"
                key={topic.id}
                href={topic.link}
                target="_blank"
                rel="noopener noreferrer"
                title={text}
              >
                {inner}
              </a>
            ) : (
              <Link className="topic-link" key={topic.id} to={topic.link} title={text}>
                {inner}
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};
